import { ignore } from 'utility/ignore';

export type ListenerHandlerFinalizer = () => void;
export type ListenerHandlerInitiator<L> = (listeners: L[]) => ListenerHandlerFinalizer;

type Entry<L> = {
    finalizer: ListenerHandlerFinalizer;
    listeners: L[];
};

export class ListenerManager<Listener> {
    private readonly _initiator: ListenerHandlerInitiator<Listener>;

    private _entry: Entry<Listener> | undefined;

    constructor(initiator: ListenerHandlerInitiator<Listener>) {
        this._initiator = initiator;
    }

    get listeners() {
        return this._entry ? this._entry.listeners : [];
    }

    forEach(cb: (listener: Listener) => void) {
        [...(this._entry?.listeners ?? [])].forEach(cb);
    }

    addListener(listener: Listener): void {
        let entry = this._entry;
        if (!entry) {
            const listeners = new Array<Listener>();
            const finalizer = this._initiator(listeners);
            entry = { finalizer, listeners };
            this._entry = entry;
        }
        entry.listeners.push(listener);
    }

    removeListener(listener: Listener): void {
        const entry = this._entry;
        if (!entry) {
            console.warn('the listener being removed is not found');
            return;
        }

        const index = entry.listeners.indexOf(listener);
        if (index == -1) {
            console.warn('the listener being removed is not found');
            return;
        }

        entry.listeners.splice(index, 1);
        const x = entry.listeners.indexOf(listener);
        if (x != -1) {
            console.warn('cannot remove the listener; something odd');
        }

        if (entry.listeners.length == 0) {
            entry.finalizer();
            this._entry = undefined;
        }
    }
}

function emptyInitiator<Listener>(listeners: Listener[]) {
    ignore(listeners);
    return () => { /* nothing to do */ };
}

export function createListenerManager<Listener>(initiator: ListenerHandlerInitiator<Listener> = emptyInitiator) {
    return new ListenerManager<Listener>(initiator);
}
